import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {ReactZoomPanPinchRef, TransformComponent, TransformWrapper, useControls} from 'react-zoom-pan-pinch';
import {useTranslation} from 'react-i18next';
import ImageControlButton from './ImageControlButton';
import './ZoomableImage.scss';

interface ZoomableImageProps {
  children: ReactNode;
  isExpanded: boolean;
  onToggleExpand: () => void;
  contentLoaded?: boolean;
  resetKey?: string;
}

interface ZoomControlsProps {
  disabled?: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const ZoomControls: React.FC<ZoomControlsProps> = ({disabled = false, isExpanded, onToggleExpand}) => {
  const {zoomIn, zoomOut, resetTransform} = useControls();
  const {t} = useTranslation();

  return (
    <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
      <ImageControlButton
        onClick={() => zoomIn()}
        disabled={disabled}
        icon="fa-magnifying-glass-plus"
        label={t('common.components.ZoomableImage.zoomIn')}
        title={t('common.components.ZoomableImage.zoomIn')}
      />
      <ImageControlButton
        onClick={() => zoomOut()}
        disabled={disabled}
        icon="fa-magnifying-glass-minus"
        label={t('common.components.ZoomableImage.zoomOut')}
        title={t('common.components.ZoomableImage.zoomOut')}
      />
      <ImageControlButton
        onClick={() => resetTransform()}
        disabled={disabled}
        icon="fa-maximize"
        label={t('common.components.ZoomableImage.resetZoom')}
        title={t('common.components.ZoomableImage.resetZoom')}
      />
      <ImageControlButton
        onClick={onToggleExpand}
        disabled={false}
        icon={isExpanded ? "fa-compress" : "fa-expand"}
        label={isExpanded ? t('common.components.ZoomableImage.collapse') : t('common.components.ZoomableImage.expand')}
        title={isExpanded ? t('common.components.ZoomableImage.collapse') : t('common.components.ZoomableImage.expand')}
      />
    </div>
  );
};

const ZoomableImage: React.FC<ZoomableImageProps> = ({
                                                       children,
                                                       isExpanded,
                                                       onToggleExpand,
                                                       contentLoaded = true,
                                                       resetKey
                                                     }) => {
  const [isPanning, setIsPanning] = useState(false);
  const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);

  useEffect(() => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.resetTransform();
    }
  }, [resetKey]);

  useEffect(() => {
    if (transformWrapperRef.current) {
      transformWrapperRef.current.resetTransform();
    }
  }, [isExpanded]);

  const handlePanningStart = () => {
    setIsPanning(true);
  };

  const handlePanningStop = () => {
    setIsPanning(false);
  };

  return (
    <div className="w-full h-full">
      <TransformWrapper
        ref={transformWrapperRef}
        initialScale={1}
        minScale={1}
        maxScale={8}
        wheel={{step: 0.1}}
        doubleClick={{mode: 'zoomIn'}}
        panning={{disabled: false}}
        centerOnInit={true}
        onPanningStart={handlePanningStart}
        onPanningStop={handlePanningStop}
      >
        {() => (
          <>
            <ZoomControls disabled={!contentLoaded} isExpanded={isExpanded} onToggleExpand={onToggleExpand}/>
            <TransformComponent
              wrapperClass="!w-full !h-full"
              contentClass="!w-full !h-full flex items-center justify-center"
              wrapperStyle={{cursor: isPanning ? 'grabbing' : 'default'}}
            >
              {children}
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default ZoomableImage;

